import React, { useEffect, useState } from "react";
import { Heart, Users, X, ArrowRight, Globe, Star, Target, Zap } from "lucide-react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";

export default function Home() {
  const [showModal, setShowModal] = useState(() => {
    // Check if modal has been shown in this session
    if (typeof window !== 'undefined') {
      return !sessionStorage.getItem('modalShown');
    }
    return true;
  });
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleCloseModal = () => {
    setShowModal(false);
    // Mark modal as shown for this session only
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('modalShown', 'true');
    }
  };

  // Navigate to donate page
  const handleDonate = () => {
    window.location.href = '/fund-our-work';
  };

  // Navigate to our story page
  const handleOurStory = () => {
    window.location.href = '/story';
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowModal(false);
      // Mark modal as shown for this session only
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('modalShown', 'true');
      }
    }, 15000);

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const carouselImages = [
    "img/introduction.jpeg",
    "img/sanyuka/8-min.jpg", 
    "img/main/kabanda.JPG",
    "img/main2.JPG",
    "img/main.JPG",
    "img/connectmain.JPG",
    "img/use99.JPG",
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  };

  const objectives = [
    {
      icon: "🛡️",
      title: "Combatting HIV/AIDS",
      description: "We are actively engaged in the fight against the spread of sexually transmitted infections, particularly HIV/AIDS, among young people. Our aim is to raise awareness and provide support to prevent and manage these health challenges.",
      gradient: "from-red-500 to-pink-600",
      bgColor: "from-red-50 to-pink-50"
    },
    {
      icon: "❤️",
      title: "Empowering Teenage Mothers", 
      description: "KCC is passionate about empowering teenage mothers, offering them the support and resources they need to lead fulfilling lives despite the challenges they face.",
      gradient: "from-purple-500 to-pink-600",
      bgColor: "from-purple-50 to-pink-50"
    },
    {
      icon: "💧",
      title: "Promoting Hygiene and Sanitation",
      description: "We prioritize the promotion of hygiene and sanitation practices within our community to improve public health and well-being.",
      gradient: "from-blue-500 to-cyan-600",
      bgColor: "from-blue-50 to-cyan-50"
    },
    {
      icon: "🎓",
      title: "Education and Innovation",
      description: "Education is a cornerstone of our work. We actively support educational initiatives and encourage innovation among young people, equipping them with the skills they need for a brighter future.",
      gradient: "from-green-500 to-emerald-600",
      bgColor: "from-green-50 to-emerald-50"
    },
    {
      icon: "🤝",
      title: "Community Support",
      description: "At KCC, we believe in the strength of community. We actively support and collaborate with the active community of Kayunga and neighboring areas. Together, we work towards fostering development and achieving both short-term and long-term goals.",
      gradient: "from-orange-500 to-amber-600",
      bgColor: "from-orange-50 to-amber-50"
    },
    {
      icon: "🔧",
      title: "Vocational Skills Development",
      description: "We provide vocational skills training to teenage mothers, youths, and individuals with physical impairments. Our goal is to empower them with practical skills that open doors to sustainable employment and economic independence.",
      gradient: "from-indigo-500 to-purple-600",
      bgColor: "from-indigo-50 to-purple-50"
    }
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 overflow-hidden pt-20 sm:pt-24">
      <Header />
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute top-96 right-20 w-48 h-48 bg-pink-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute bottom-32 left-1/4 w-32 h-32 bg-blue-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
        
        {/* Floating organic shapes */}
        <div className="absolute top-1/3 left-0 w-96 h-96 opacity-20">
          <svg viewBox="0 0 200 200" className="w-full h-full text-purple-300 animate-spin" style={{animationDuration: '20s'}}>
            <path fill="currentColor" d="M44.7,-76.4C58.8,-69.2,71.8,-57.1,79.6,-41.8C87.4,-26.6,90,-8.2,87.4,9.1C84.8,26.4,77,42.6,63.2,53.8C49.4,65,29.6,71.2,8.9,76.3C-11.8,81.4,-33.4,85.4,-49.8,78.1C-66.2,70.8,-77.4,52.2,-84.1,31.1C-90.8,10,-93,13.5,-89.7,-8.8C-86.4,-31.1,-77.6,-58.2,-61.2,-65.4C-44.8,-72.6,-20.8,-59.9,-0.1,-59.8C20.6,-59.7,30.6,-83.6,44.7,-76.4Z"/>
          </svg>
        </div>
      </div>

      {/* Enhanced Responsive Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="relative w-full max-w-4xl max-h-[95vh] overflow-hidden">
            <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-white/20">
              {/* Modal Header - Responsive */}
              <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 p-4 md:p-6">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/90 via-pink-600/90 to-red-600/90"></div>
                
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 md:w-12 md:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <Heart className="w-5 h-5 md:w-6 md:h-6 text-white" />
                    </div>
                    <h2 className="text-lg md:text-2xl font-bold text-white">
                      Kayunga Community Club
                    </h2>
                  </div>
                  <button 
                    onClick={handleCloseModal}
                    className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-xl flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Modal Body - Responsive */}
              <div className="p-4 md:p-8 max-h-[70vh] overflow-y-auto">
                <div className="text-center mb-6">
                  <img
                    src="img/introduction.jpeg"
                    alt="NGO Introduction"
                    className="w-full max-w-md h-48 md:h-60 object-cover rounded-2xl mx-auto shadow-lg mb-6"
                  />
                </div>

                <p className="text-center text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                  Thank you for visiting the Kayunga Community Club website. We are
                  dedicated to making a positive impact on the Kayunga community through
                  various initiatives and projects.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 md:p-6 rounded-2xl border border-purple-100">
                    <h5 className="text-purple-800 font-bold mb-3 flex items-center gap-2 text-sm md:text-base">
                      <Star className="w-4 h-4" /> Our Mission
                    </h5>
                    <p className="text-purple-700 font-semibold text-sm md:text-base">
                      Creating A Community Free From Adversity
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 md:p-6 rounded-2xl border border-blue-100">
                    <h5 className="text-blue-800 font-bold mb-3 flex items-center gap-2 text-sm md:text-base">
                      <Users className="w-4 h-4" /> Get Involved
                    </h5>
                    <p className="text-blue-700 text-sm md:text-base">
                      You can be a part of our journey by volunteering, donating, or supporting
                      our projects. Together, we can make a difference.
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 md:p-8 pt-0">
                <button 
                  onClick={handleCloseModal}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 md:py-4 px-6 rounded-2xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative z-10 text-center py-8 md:py-16 px-4 mt-4 sm:mt-0">
        <div className="relative inline-block mb-8">
          <h3 className="text-3xl md:text-5xl lg:text-6xl font-black bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent mb-6 transform hover:scale-105 transition-transform duration-300">
            Support Kayunga Community Club
          </h3>
          
          {/* Decorative organic shapes */}
          <div className="absolute -top-4 -left-4 w-8 h-8 bg-purple-400/30 rounded-full transform rotate-12 animate-bounce"></div>
          <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-pink-400/30 rounded-full animate-pulse"></div>
        </div>
        
        <div className="w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto mb-4"></div>
        <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
          Join us in creating lasting change in the Kayunga community
        </p>
      </div>

      {/* Enhanced Carousel */}
      <div className="relative max-w-7xl mx-auto px-4 mb-16">
        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-10 pointer-events-none"></div>
          
          <div className="relative h-64 md:h-96 lg:h-[500px]">
            <img
              src={carouselImages[currentSlide]}
              alt={`Community Impact ${currentSlide + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Carousel indicators */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
              {carouselImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'bg-white scale-125' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
            
            {/* Navigation buttons */}
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 z-20"
            >
              <ArrowRight className="w-6 h-6 text-white transform rotate-180" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 z-20"
            >
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="relative max-w-7xl mx-auto px-4 pb-20">
        {/* Title Section */}
        <div className="text-center mb-20">
          <div className="relative inline-block mb-8">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-black bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent transform hover:scale-105 transition-transform duration-300">
              Kayunga Community Club
            </h1>
            
            {/* Floating decorative elements */}
            <div className="absolute -top-4 -left-4 w-8 h-8 bg-purple-400/30 rounded-lg transform rotate-12 animate-bounce"></div>
            <div className="absolute -bottom-4 -right-4 w-6 h-6 bg-pink-400/30 rounded-full animate-pulse"></div>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <div className="relative bg-white/60 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-6 md:p-12">
              <p className="text-lg md:text-2xl text-gray-700 leading-relaxed mb-6">
                <span className="font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Kayunga Community Club</span> (KCC) is a youth initiative founded in 2018 with a mission to enhance the quality of life for marginalized communities and young people. Our organization is deeply committed to fostering friendly relations, promoting development, and addressing pressing issues in our community.
              </p>
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-100 to-pink-100 px-6 py-3 rounded-2xl border border-purple-200">
                <Target className="w-6 h-6 text-purple-600" />
                <span className="text-lg font-semibold text-gray-800">
                  We are dedicated to the following key objectives:
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Objectives Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {objectives.map((objective, index) => (
            <div
              key={index}
              className="group transform transition-all duration-500 hover:scale-105 hover:-translate-y-2"
            >
              <div className={`relative bg-gradient-to-br ${objective.bgColor} rounded-3xl p-6 md:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-white/50 overflow-hidden`}>
                {/* Background pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 opacity-20 transform translate-x-6 -translate-y-6">
                  <div className={`w-full h-full bg-gradient-to-br ${objective.gradient} rounded-full blur-2xl`}></div>
                </div>
                
                {/* Number badge */}
                <div className={`absolute top-4 left-4 w-12 h-12 bg-gradient-to-br ${objective.gradient} rounded-2xl flex items-center justify-center text-white font-bold text-lg shadow-lg transform group-hover:scale-110 transition-transform duration-300`}>
                  {index + 1}
                </div>
                
                <div className="relative pt-8">
                  {/* Icon */}
                  <div className="text-5xl md:text-6xl mb-6 transform group-hover:scale-110 transition-transform duration-300">
                    {objective.icon}
                  </div>
                  
                  <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-4">
                    {objective.title}
                  </h3>
                  
                  <p className="text-gray-700 leading-relaxed text-base md:text-lg">
                    {objective.description}
                  </p>

                  {/* Animated bottom border */}
                  <div className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${objective.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-full`}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Philosophy Section */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-3xl blur-2xl opacity-20"></div>
          
          <div className="relative bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-3xl p-1 shadow-2xl">
            <div className="bg-white rounded-3xl p-6 md:p-16">
              <div className="text-center mb-12">
                <div className="inline-flex items-center justify-center w-20 md:w-24 h-20 md:h-24 bg-gradient-to-br from-purple-600 to-pink-600 rounded-2xl text-white mb-8 shadow-2xl transform hover:scale-110 transition-transform duration-300">
                  <Globe className="w-8 md:w-10 h-8 md:h-10" />
                </div>
                
                <h2 className="text-3xl md:text-5xl font-black bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent mb-6">
                  Our Philosophy
                </h2>
                
                <div className="w-32 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 rounded-full mx-auto"></div>
              </div>
              
              <div className="max-w-4xl mx-auto">
                <div className="relative">
                  <div className="absolute -left-6 top-0 w-1 h-full bg-gradient-to-b from-purple-500 via-pink-500 to-red-500 rounded-full"></div>
                  
                  <p className="text-lg md:text-2xl text-gray-700 leading-relaxed font-medium pl-12">
                    Our service philosophy is driven by our extensive experience in community engagement. We believe that every challenge has a solution, and we work hand in hand with other service organizations to address the unique needs of our communities. Our unwavering dedication to improving, building, and developing communities underscores our mission.
                  </p>
                </div>

                {/* Call to action */}
                <div className="mt-12 text-center">
                  <div className="inline-flex items-center gap-4 bg-gradient-to-r from-purple-100 via-pink-100 to-red-100 px-8 py-4 rounded-2xl border border-purple-200">
                    <Globe className="w-8 h-8 text-purple-600" />
                    <span className="text-lg md:text-xl font-semibold text-gray-800">
                      Creating lasting impact through innovative community solutions
                    </span>
                    <Zap className="w-8 h-8 text-pink-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Floating particles animation */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-30 animate-ping`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 2}s`,
              animationDuration: `${4 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>
      <Footer></Footer>
    </div>
  );
}